// Flight booking management service (ticketing update / modify / edit flow).
// Migrated onto the API gateway: both calls now go through apiClient (encrypted
// request + decrypted response, beadmin auth headers) on /app/v1/beadmin paths,
// instead of hitting the backend directly via REACT_APP_DEV_URL/PROD_URL.
//   fetchBookingDetails -> POST /app/v1/beadmin/flight/booking/fetch-details
//                          (engine POST /agent/v1/booking/flight/fetchDetails)
//   updateFlightBooking -> POST /app/v1/beadmin/flight/booking/update
//                          (engine POST /agent/v1/booking/flight/update)
// The gateway preserves the legacy response shape, so the return values are
// unchanged for callers.
import { apiPost } from "./apiClient";
import { BE_ADMIN_BASE } from "./environment";

/**
 * Fetch flight booking details for the management flow.
 * @param {string} requestType The action type (Ticketing Update, Modify, etc.)
 * @param {string} bookingId The booking / itinerary id to retrieve
 * @returns {Promise} The booking data
 */
export async function fetchBookingDetails(requestType, bookingId) {
    try {
        const res = await apiPost(`${BE_ADMIN_BASE}/flight/booking/fetch-details`, {
            requestType,
            requestId: bookingId,
        });
        // The gateway wraps the engine response as { statusCode, message, data },
        // where data = { status, message, data: <payload> }. Success = statusCode
        // 200; the booking payload is at res.data.data.
        if (res?.statusCode !== 200) {
            throw new Error(res?.data?.message || res?.message || "Failed to retrieve booking details");
        }
        return res?.data?.data;
    } catch (error) {
        const message =
            error?.response?.data?.message || error?.message || "Failed to retrieve booking details";
        console.error("[fetchBookingDetails] error:", message);
        throw new Error(message);
    }
}

/**
 * Commit a flight booking change (ticketing update, modify, etc.).
 * @param {string} requestType The action type
 * @param {string} requestId The itinerary id
 * @param {object} data The change payload
 * @returns {Promise} The updated booking data
 */
export async function updateFlightBooking(requestType, requestId, data) {
    try {
        const res = await apiPost(`${BE_ADMIN_BASE}/flight/booking/update`, {
            requestType,
            itineraryId: requestId,
            data,
        });
        // Gateway envelope: { statusCode, message, data: { status, message, data } }.
        if (res?.statusCode !== 200) {
            throw new Error(res?.data?.message || res?.message || "Failed to update booking details");
        }
        return res?.data?.data;
    } catch (error) {
        const message =
            error?.response?.data?.message || error?.message || "Failed to update booking details";
        console.error("[updateFlightBooking] error:", message);
        throw new Error(message);
    }
}
